/**
 * Что делать, когда стартовый /api/users/me не вернул профиль.
 * Токены стирает только apiFetch, когда отказ сессии подтверждён (401 + refresh
 * invalid). Сеть/таймаут/5xx — транзиентный сбой: после возврата со страницы
 * оплаты запросы часто падают разово, и это не повод разлогинивать.
 */
export const ME_MAX_RETRIES = 2;
export const ME_RETRY_DELAYS_MS = [1000, 2000];
/** Повторяем, только пока с начала загрузки прошло не больше (ограничивает спиннер). */
export const ME_RETRY_BUDGET_MS = 10_000;

export type MeFailureAction = "retry" | "expired" | "keep";

export function meFailureAction(retriesDone: number, tokensStillPresent: boolean): MeFailureAction {
    if (!tokensStillPresent) return "expired";
    return retriesDone < ME_MAX_RETRIES ? "retry" : "keep";
}

export type MeLoadResult<T> =
    | {kind: "ok"; data: T}
    | {kind: "expired"}
    | {kind: "keep"}
    | {kind: "stale"};

export interface MeLoadDeps<T> {
    fetchMe: () => Promise<{data: T | null}>;
    tokensPresent: () => boolean;
    sleep: (ms: number) => Promise<void>;
    now: () => number;
    /** Поколение устарело (запущена более новая загрузка) — результат не применять. */
    isStale: () => boolean;
}

/** /me с повторами на транзиентных сбоях в пределах бюджета времени. */
export async function loadMeWithRetry<T>(deps: MeLoadDeps<T>): Promise<MeLoadResult<T>> {
    const startedAt = deps.now();
    let retries = 0;
    for (;;) {
        const response = await deps.fetchMe();
        if (deps.isStale()) return {kind: "stale"};
        if (response.data) return {kind: "ok", data: response.data};
        let action = meFailureAction(retries, deps.tokensPresent());
        const delay = ME_RETRY_DELAYS_MS[retries] ?? 2000;
        if (action === "retry" && deps.now() - startedAt + delay > ME_RETRY_BUDGET_MS) action = "keep";
        if (action !== "retry") return {kind: action};
        await deps.sleep(delay);
        if (deps.isStale()) return {kind: "stale"};
        retries += 1;
    }
}
